var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Link, Breadcrumbs as MuiBreadcrumbs, Skeleton, Typography, } from "@mui/material";
import { useContext } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { UserContext } from "../auth/UserProvider";
import { routeConfig, } from "./routeConfig";
import { useBreadcrumbData } from "./useBreadcrumbData";
/**
 * Normalizes a path by removing trailing slashes (except for root).
 */
var normalizePath = function (path) {
    if (path === "/")
        return path;
    return path.replace(/\/+$/, "");
};
/**
 * Matches a URL path against a route pattern and extracts parameter values.
 * Returns null if no match, or an object with extracted params if matched.
 */
var matchRoutePattern = function (pathname, pattern) {
    var normalizedPath = normalizePath(pathname);
    var normalizedPattern = normalizePath(pattern);
    var pathParts = normalizedPath.split("/").filter(Boolean);
    var patternParts = normalizedPattern.split("/").filter(Boolean);
    if (pathParts.length !== patternParts.length) {
        return null;
    }
    var params = {};
    for (var i = 0; i < patternParts.length; i++) {
        var patternPart = patternParts[i];
        var pathPart = pathParts[i];
        if (patternPart.startsWith(":")) {
            // This is a parameter
            var paramName = patternPart.slice(1);
            params[paramName] = pathPart;
        }
        else if (patternPart !== pathPart) {
            // Static segment doesn't match
            return null;
        }
    }
    return params;
};
/**
 * Finds the best matching route config for a given path.
 * Returns the config and extracted parameters.
 */
var findRouteConfig = function (pathname) {
    var e_1, _a;
    var normalizedPath = normalizePath(pathname);
    try {
        for (var _b = __values(Object.keys(routeConfig)), _c = _b.next(); !_c.done; _c = _b.next()) {
            var pattern = _c.value;
            var params = matchRoutePattern(normalizedPath, pattern);
            if (params !== null) {
                var config = routeConfig[pattern];
                if (config) {
                    return { config: config, params: params };
                }
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return null;
};
/**
 * Parses the current URL into breadcrumb segments.
 * Builds up the path progressively to create the full breadcrumb trail.
 */
var parsePathSegments = function (pathname) {
    var normalizedPath = normalizePath(pathname);
    if (normalizedPath === "/") {
        return [
            {
                path: "/",
                fullPath: "/",
                label: "Home",
                isLast: true,
                isLinkable: true,
            },
        ];
    }
    var parts = normalizedPath.split("/").filter(Boolean);
    var segments = [];
    // Always start with Home
    segments.push({
        path: "/",
        fullPath: "/",
        label: "Home",
        isLast: false,
        isLinkable: true,
    });
    // Build up each segment
    var currentPath = "";
    for (var i = 0; i < parts.length; i++) {
        var part = parts[i];
        currentPath += "/" + part;
        var isLast = i === parts.length - 1;
        var match = findRouteConfig(currentPath);
        if (match) {
            segments.push({
                path: part,
                fullPath: currentPath,
                label: match.config.label,
                resolver: match.config.resolver,
                isLast: isLast,
                isLinkable: true,
            });
        }
        else {
            // No route config for this path - show label but don't make it a link
            // Format the segment name: "players" -> "Players", "external-users" -> "External Users"
            var formattedLabel = part
                .split("-")
                .map(function (word) { return word.charAt(0).toUpperCase() + word.slice(1); })
                .join(" ");
            segments.push({
                path: part,
                fullPath: currentPath,
                label: formattedLabel,
                isLast: isLast,
                isLinkable: false,
            });
        }
    }
    return segments;
};
/**
 * Extracts segments that need entity resolution for the data hook.
 */
var getResolverSegments = function (segments) {
    return segments
        .filter(function (s) { return s.resolver; })
        .map(function (s) {
        var _a;
        // Extract the numeric ID from the path
        var pathParts = s.fullPath.split("/");
        var idPart = (_a = pathParts[pathParts.length - 1]) !== null && _a !== void 0 ? _a : "";
        var id = parseInt(idPart, 10);
        // If the ID is not in the last position, find it
        if (isNaN(id)) {
            // Look for numeric segment in the path
            for (var i = pathParts.length - 1; i >= 0; i--) {
                var part = pathParts[i];
                if (part) {
                    var num = parseInt(part, 10);
                    if (!isNaN(num)) {
                        return { resolver: s.resolver, value: num };
                    }
                }
            }
        }
        return { resolver: s.resolver, value: id };
    })
        .filter(function (s) { return !isNaN(s.value); });
};
export var Breadcrumbs = function () {
    var user = useContext(UserContext).user;
    var location = useLocation();
    var segments = parsePathSegments(location.pathname);
    var resolverSegments = getResolverSegments(segments);
    var resolvedNames = useBreadcrumbData(resolverSegments);
    // Don't show breadcrumbs on home page or for unauthenticated users
    if (!user || location.pathname === "/" || segments.length <= 1) {
        return null;
    }
    var getDisplayLabel = function (segment) {
        if (segment.resolver) {
            var name_1 = resolvedNames[segment.resolver];
            if (resolvedNames.isLoading && !name_1) {
                return _jsx(Skeleton, { width: 80 });
            }
            return name_1 || segment.label;
        }
        return segment.label;
    };
    return (_jsx(MuiBreadcrumbs, { separator: _jsx(NavigateNextIcon, { fontSize: "small" }), "aria-label": "breadcrumb", maxItems: 5, itemsBeforeCollapse: 1, itemsAfterCollapse: 2, sx: { mb: 2 }, children: segments.map(function (segment) {
            var label = getDisplayLabel(segment);
            var isHome = segment.fullPath === "/";
            // Last segment or non-linkable segments are rendered as text
            if (segment.isLast || !segment.isLinkable) {
                return (_jsx(Typography, { color: segment.isLast ? "text.primary" : "inherit", sx: {
                        display: "flex",
                        alignItems: "center",
                        maxWidth: { xs: 150, sm: 250, md: "none" },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }, children: label }, segment.fullPath));
            }
            return (_jsxs(Link, { component: RouterLink, to: segment.fullPath, underline: "hover", color: "inherit", sx: {
                    display: "flex",
                    alignItems: "center",
                    maxWidth: { xs: 150, sm: 250, md: "none" },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }, children: [isHome && _jsx(HomeIcon, { sx: { mr: 0.5 }, fontSize: "small" }), label] }, segment.fullPath));
        }) }));
};
// Export for testing and reuse
export { findRouteConfig, getResolverSegments, matchRoutePattern, normalizePath, parsePathSegments, };
